import React, { useMemo, useRef } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";
import UFOSegment from "@atlassian/wac-ufo-segment";

import { ButtonColor } from "@/components/adg4/button/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/";
import { LogoV2 } from "@/libraries/wpl/foundations/src/logov2";

import { useMore } from "./hooks/use-more";
import { MobileGlobalNav } from "./mobile-nav/mobile-nav";
import GlobalNavLinksContainer, {
    GlobalNavLinkContentProps,
    GlobalNavProvider,
    SubnavLayout
} from "./nav-links-container";
import SearchAndProfile from "./nav-search-and-profile";
import { ProductSubnav } from "./product-subnav";
import { ResourcesSubnav } from "./resources-subnav";
import SearchBar from "./search-bar";
import { TeamsSubnav } from "./teams-subnav";
import { WhyAtlassianSubnav } from "./why-atlassian-subnav";

// TODO: this is a copy fron v1 of global nav need to be updated
const navigationBarWrapperCss = css({
    "alignItems": "center",
    "background": "#ffffff",
    "display": "flex",
    "flexDirection": "row",
    "justifyContent": "center",
    "zIndex": 5,
    "height": "68px",
    [media.below.md]: {
        ".showMenu &": {
            background: "#f4f5f7",
            height: "100vh",
            overflow: "auto",
            position: "fixed",
            insetBlockStart: 0,
            width: "100%"
        }
    },
    ".logged-in-out-user-profile": {
        border: "thin solid #0052cc",
        borderRadius: "25px 25px 25px 25px"
    },
    ".navigation-bar": {
        "display": "none",
        "alignItems": "center",
        "justifyContent": "center",
        "flex": 1,
        "paddingInline": "40px",
        "paddingBlock": token("space.200"),
        "flexDirection": "row",
        "position": "relative",
        "zIndex": 6,
        "marginInline": "24px",

        [media.above.md]: {
            display: "flex",
            maxWidth: "1152px",
            paddingInline: "0"
        },
        [media.above.md]: {
            marginInline: "60px"
        },

        ".search-bar": {
            marginInlineStart: "25px",
            marginInlineEnd: "25px",
            width: "100%"
        }
    },
    ".navigation-bar-logo": {
        "display": "flex",
        "alignItems": "center",
        [media.above.md]: {
            width: "auto"
        },
        ".navigation-bar-logo-link": {
            "display": "flex",
            "> svg": {
                height: "auto",
                transition: "opacity 250ms",
                width: "187px"
            }
        }
    },

    ".navigation-links": {
        "display": "grid",
        "gridTemplateColumns": "repeat(5, max-content)",
        "gap": token("space.400"),
        "alignItems": "end",
        "listStyle": "none",
        "margin": 0,
        "padding": 0,
        "marginInlineStart": token("space.250"),
        ".chevron-icon": {
            display: "block",
            color: "#0052cc",
            marginInlineStart: "8px",
            lineHeight: 0
        },
        "> li": {
            "> a.nav-link-anchor, > span.globalnav-more-less": {
                "display": "flex",
                "alignItems": "center",
                "textDecoration": "none",
                "lineHeight": "1rem",
                "marginBlockEnd": "-1px",
                "&.isActive": {
                    color: "#0052cc"
                }
            },
            ".globalnav-more-less": {
                "cursor": "pointer",

                "&:hover": {
                    color: "#0747A6"
                }
            },
            [media.above.md]: {
                "> a.nav-link-anchor": {
                    borderRadius: "3px",
                    textDecoration: "none"
                }
            }
        },
        [media.above.md]: {
            marginInlineStart: token("space.400")
        }
    },

    ".navigation-search": {
        display: "flex",
        alignItems: "center"
    },

    ".navigation-action": {
        "alignItems": "center",
        "display": "flex",
        "flexDirection": "row",
        "listStyle": "none",
        "margin": 0,
        "padding": 0,
        "> li": {
            alignItems: "center",
            display: "flex",
            flexDirection: "row"
        }
    },

    ".navigation-cart-button": {
        "paddingInlineStart": "0 !important",
        "> div": {
            marginInlineStart: "20px",
            height: "30px",
            insetBlockStart: "8px",
            position: "relative"
        },
        "> div:empty": {
            margin: 0,
            height: 0,
            insetBlockStart: 0
        }
    }
});

const mobileWrapperCss = css({
    "position": "relative",
    "zIndex": "5",
    ".mobile-nav": {
        display: "none"
    },

    [media.below.md]: {
        ".desktop-nav": {
            display: "none"
        },

        ".mobile-nav": {
            display: "block"
        }
    }
});

export type GlobalNavProps = {
    productKey: ProductKeyTypes;
    isShowSearchBarAndProfileContainer?: boolean;
    isShowTryNowLink?: boolean;
    isShowSearchButton?: boolean;
    tryNowLinkCtaButtonColor?: ButtonColor;
};

export type GlobalNavNavigationBarProps = GlobalNavProps & {
    globalLinksProps: GlobalNavLinkContentProps[];
};

export const GlobalNavNavigationBar = ({
    productKey,
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = false,
    isShowSearchButton = true,
    tryNowLinkCtaButtonColor,
    globalLinksProps
}: GlobalNavNavigationBarProps) => {
    const intl = useIntl();
    const [isOpenSearch, setIsOpenSearch] = React.useState(false);
    const globalLinksRef = useRef<HTMLUListElement>(null);
    const searchAndProfileRef = useRef<HTMLDivElement>(null);

    const {
        showMoreItems: setShowMore,
        shouldShowMoreItems: showMore,
        maxItems: maxLinks
    } = useMore({
        leftElementRef: globalLinksRef,
        rightElementRef: searchAndProfileRef,
        initialMaxItems: globalLinksProps.length
    });

    return (
        <div css={navigationBarWrapperCss} className="desktop-nav">
            <div className="navigation-bar">
                <nav
                    css={css({
                        display: "flex",
                        flexGrow: 1,
                        flexShrink: 1,
                        flexBasis: "0%"
                    })}
                >
                    <div className="navigation-bar-logo">
                        <a
                            className="navigation-bar-logo-link"
                            href={useLocalizedHref("/")}
                            aria-label={intl.formatMessage({
                                id: "wacGlobalNavigation.logo-label",
                                defaultMessage: "Atlassian logo"
                            })}
                            data-event="clicked"
                            data-event-component="linkButton"
                            data-event-container="globalNavWac"
                            data-product-key={productKey}
                        >
                            <LogoV2
                                brand="atlassian"
                                type="logo"
                                appearance="brand"
                            />
                        </a>
                    </div>
                    {isOpenSearch ? (
                        <SearchBar setIsOpenSearch={setIsOpenSearch} />
                    ) : (
                        <GlobalNavLinksContainer
                            globalLinksProps={globalLinksProps}
                            globalLinksRef={globalLinksRef}
                            maxLinks={maxLinks}
                            showMore={showMore}
                            setShowMore={setShowMore}
                        />
                    )}
                </nav>
                {!isOpenSearch &&
                    isShowSearchBarAndProfileContainer &&
                    !showMore && (
                        <SearchAndProfile
                            isShowTryNowLink={isShowTryNowLink}
                            ctaButtonColor={tryNowLinkCtaButtonColor}
                            productKey={productKey}
                            isShowSearchButton={isShowSearchButton}
                            setIsOpenSearch={setIsOpenSearch}
                            searchAndProfileRef={searchAndProfileRef}
                        />
                    )}
            </div>
        </div>
    );
};

export const GlobalNav = ({
    productKey = "home",
    isShowSearchBarAndProfileContainer = true,
    isShowTryNowLink = false,
    isShowSearchButton = true,
    tryNowLinkCtaButtonColor
}: GlobalNavProps) => {
    const intl = useIntl();

    const enterpriseHref = useLocalizedHref("/enterprise");

    const globalLinksProps: GlobalNavLinkContentProps[] = useMemo(
        () => [
            {
                globalLinksKey: "products",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.products",
                    defaultMessage: "Products"
                }),
                hasSubnav: true,
                content: <ProductSubnav productKey={productKey} />,
                layout: SubnavLayout.Product
            },
            {
                globalLinksKey: "teams",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.teams",
                    defaultMessage: "Teams"
                }),
                hasSubnav: true,
                content: <TeamsSubnav productKey={productKey} />,
                layout: SubnavLayout.Teams
            },
            {
                globalLinksKey: "why-atlassian",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.whyAtlassian",
                    defaultMessage: "Why Atlassian?"
                }),
                hasSubnav: true,
                content: <WhyAtlassianSubnav productKey={productKey} />,
                layout: SubnavLayout.WhyAtlassian
            },
            {
                globalLinksKey: "resources",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.resources",
                    defaultMessage: "Resources"
                }),
                hasSubnav: true,
                content: <ResourcesSubnav productKey={productKey} />,
                layout: SubnavLayout.Resources
            },
            {
                globalLinksKey: "enterprise",
                linkText: intl.formatMessage({
                    id: "globalNavigation.Navlink.enterprise",
                    defaultMessage: "Enterprise"
                }),
                hasSubnav: false,
                href: enterpriseHref
            }
        ],
        [enterpriseHref, intl]
    );

    const itemList = useMemo(() => {
        return globalLinksProps.map((link) => link.globalLinksKey);
    }, [globalLinksProps]);

    return (
        <UFOSegment name="global-nav-v2">
            <div css={mobileWrapperCss}>
                <GlobalNavProvider itemList={itemList}>
                    <GlobalNavNavigationBar
                        productKey={productKey}
                        isShowSearchBarAndProfileContainer={
                            isShowSearchBarAndProfileContainer
                        }
                        isShowTryNowLink={isShowTryNowLink}
                        isShowSearchButton={isShowSearchButton}
                        tryNowLinkCtaButtonColor={tryNowLinkCtaButtonColor}
                        globalLinksProps={globalLinksProps}
                    />
                </GlobalNavProvider>
                <div className="mobile-nav">
                    <MobileGlobalNav productKey={productKey} />
                </div>
            </div>
        </UFOSegment>
    );
};

GlobalNav.displayName = "GlobalNav";
